import React from "react";
import styles from "./SelectCard.module.css";

/**
 * SelectCard — a toggleable card for multi/single select groups.
 *
 * Props:
 *   label      {string}   — display text (may include emoji if emoji prop is omitted)
 *   emoji      {string}   — optional emoji/icon rendered separately before label
 *   selected   {boolean}  — whether the card is in selected state
 *   disabled   {boolean}  — disables interaction; renders dimmed
 *   onClick    {fn}       — called when the card is clicked (not called when disabled)
 *   showCheck  {boolean}  — show ✓ checkmark when selected (default true)
 *   size       {string}   — "sm" | "md" | "lg" (default "md")
 *   style      {object}   — inline style override
 *   className  {string}   — extra class names
 */
const SelectCard = ({
  label,
  emoji,
  selected = false,
  disabled = false,
  onClick,
  showCheck = true,
  size = "md",
  style,
  className = "",
}) => {
  const handleClick = () => {
    if (!disabled && onClick) onClick();
  };

  const stateClass = disabled
    ? styles.disabled
    : selected
    ? styles.selected
    : styles.unselected;

  return (
    <div
      className={[
        styles.card,
        styles[size] || "",
        stateClass,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={handleClick}
      style={style}
      role="button"
      aria-pressed={selected}
      aria-disabled={disabled}
    >
      {showCheck && selected && !disabled && (
        <span className={styles.check}>{"✓"}</span>
      )}
      {emoji && <span className={styles.icon}>{emoji}</span>}
      <span className={styles.label}>{label}</span>
    </div>
  );
};

export default SelectCard;
